import React, { useEffect, useState } from "react";
import App from "app";
import { Box } from "@mui/material";
import { StriimTypography } from "@striim/striim-ui-v2";
import {
    categoryLabelStyles,
    errorIconStyles,
    flowIconStyles,
    FlowIconStyles,
    handleStyles,
    nodeContentStyles,
    nodeIconStyles,
    nodeLabelStyles,
    statStyle
} from "./node.styles";
import AdapterNativeIcon from "src/generic/icon/component-icon/adapter-native-icon";
import { ComponentKeys } from "core/services/componentService/component-service-base";
import { Handle, Position, useUpdateNodeInternals } from "reactflow";
import metaObjectHelper, {
    resolveSourceValidationDscription
} from "app/components/common/editor/meta-object-editors/metaObjectHelper";
import licenseService from "core/services/metaStoreService/license-service";
import Components from "../../../../../resources/generated/components.json";
import ErrorIcon from "src/generic/icon/wizard-icon/errorFilled.svg";
import { useAppSelector } from "src/stores/redux/hooks";
import { getAppFQN, getAppStatus, getCurrentLayout, getEdges } from "../helpers/selectors";
import store from "src/stores/redux/store";
import numeral from "numeral";
import { TwoDecimalsAndPrefix } from "core/utils/number-format";

// Inlined from removed node-utils.tsx
const isAddOnAdapter = (adapter: string): boolean => {
    const sql2FabricAddOnAdapters = [
        Components.DeltaLakeWriter[ComponentKeys.Adapter],
        Components.FabricDataWarehouseWriter[ComponentKeys.Adapter]
    ];
    return sql2FabricAddOnAdapters.includes(adapter) && licenseService.isLiteLicense;
};

const Description = ({ description }) => {
    return (
        <Box sx={nodeLabelStyles}>
            {typeof description !== "string" && description?.title ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {description.icon && <description.icon />}
                    <StriimTypography variant="caption" data-test-id="node-description">
                        <div dangerouslySetInnerHTML={{ __html: description.title }} />
                    </StriimTypography>
                </Box>
            ) : (
                <StriimTypography
                    variant="caption3"
                    color="greyscale.700"
                    fontSize={"12px"}
                    data-test-id="node-description"
                >
                    {description}
                </StriimTypography>
            )}
        </Box>
    );
};

export const NameAndDescription = ({ name, description }) => {
    return (
        <Box display="flex" flexDirection="column">
            <StriimTypography id={`node-name-${name}`} variant="caption" color="greyscale.900" sx={nodeLabelStyles}>
                {name}
            </StriimTypography>
            <Description description={description} />
        </Box>
    );
};

const FlowIcon = () => <Box sx={FlowIconStyles} className="icon-flow" />;

export const NodeIcon = ({ data, showBorder = false, showBgColor = false }) => {
    // Handle FLOW type specifically
    if (data.componentType === "FLOW")
        return (
            <Box sx={showBgColor ? flowIconStyles : {}}>
                <FlowIcon />
            </Box>
        );
    // Use the same logic as the old flow designer for other types
    if (!data.metaObject) return null;
    // Get the component info using the metaObjectHelper (same as old FD)
    const componentInfo =
        data.metaObjectId === "temp-node"
            ? Components[data.componentId]
            : metaObjectHelper.getIconClassByMetaObject(data.metaObject, true);

    if (componentInfo && typeof componentInfo === "object") {
        return (
            <Box sx={showBgColor ? nodeIconStyles : {}}>
                <AdapterNativeIcon
                    name={componentInfo[ComponentKeys.ComponentName] || componentInfo.name || ""}
                    type={componentInfo[ComponentKeys.Type] || data.type}
                    size={38}
                    spriteId={componentInfo[ComponentKeys.SpriteID]}
                    showBorder={showBorder}
                    isAddOn={isAddOnAdapter(componentInfo[ComponentKeys.Adapter])}
                />
            </Box>
        );
    } else if (typeof componentInfo === "string") {
        return (
            <Box sx={showBgColor ? nodeIconStyles : {}}>
                <AdapterNativeIcon name={componentInfo} type={data.type} size={38} showBorder={showBorder} />
            </Box>
        );
    }
};

const getDescriptionForTempNode = validationResult => {
    const { titleHtml, icon } = resolveSourceValidationDscription(validationResult.status);
    return { title: titleHtml, description: validationResult.message, icon };
};

export const NodeContent = ({ data, hideDescription = false }) => {
    const [description, setDescription] = useState(null);
    const appFQN = useAppSelector(getAppFQN);

    useEffect(() => {
        (async () => {
            if (hideDescription) return;
            const desc = await metaObjectHelper.descriptionResolver(data.metaObject, appFQN);
            setDescription(desc);
        })();
    }, [data]);

    useEffect(() => {
        if (hideDescription) return;
        const handler = async (validationResult: { modelId: string }) => {
            if (validationResult.modelId === "temp-node" && data.metaObjectId === "temp-node") {
                const desc = getDescriptionForTempNode(validationResult);
                setDescription(desc);
                return;
            }
            if (validationResult.modelId !== data.metaObject?.id) return;
            const desc = await metaObjectHelper.descriptionResolver(data.metaObject, appFQN);
            setDescription(desc);
        };
        App.vent.on("component:validation", handler);
        return () => App.vent.off("component:validation", handler);
    }, [data, appFQN, hideDescription]);

    return (
        <Box sx={nodeContentStyles}>
            {/* Icon */}
            <NodeIcon data={data} showBgColor={true} />
            <NameAndDescription name={data.label} description={description} />
        </Box>
    );
};

const getCategoryLabel = (type: string): string => {
    const categoryMap: Record<string, string> = {
        SOURCE: "SOURCE",
        TARGET: "TARGET",
        CACHE: "CACHE",
        EXTERNALCACHE: "EXTERNAL CACHE",
        CQ: "CQ",
        WINDOW: "WINDOW",
        PROCESSOR: "PROCESSOR",
        ROUTER: "ROUTER",
        SENTINEL: "SENTINEL",
        WACTIONSTORE: "WACTIONSTORE",
        FLOW: "FLOW"
    };
    return categoryMap[type?.toUpperCase()] || type?.toUpperCase() || "";
};

const showCategory = (type: string): boolean => ["SOURCE", "TARGET"].includes(type);

export const NodeCategory = ({ type, selected }) => {
    if (!showCategory(type)) return null;

    const appStatus = getAppStatus(store.getState());
    return (
        <StriimTypography
            variant="caption2"
            color="greyscale.500"
            textTransform="uppercase"
            fontWeight={600}
            sx={categoryLabelStyles(appStatus === "RUNNING", type)}
        >
            {getCategoryLabel(type)}
        </StriimTypography>
    );
};

const formatNumber = (num: number): string => numeral(num).format(TwoDecimalsAndPrefix);

const getStat = stats => {
    // Check if recentDataRate is available
    const mostRecentData = stats?.["most-recent-data"];
    const recentDataRate = stats?.["most-recent-data"].rate;
    // Statistics not assigned
    if (recentDataRate === -1) return "";
    // Server returned empty value for statistics
    if (typeof recentDataRate === "undefined" || recentDataRate === null) return "Fetching…";

    // Format the main rate message
    let message = formatNumber(recentDataRate) + " records/s";

    // For CACHE components, show cache size if available
    if (stats.type === "CACHE" && mostRecentData?.["cache-size"] !== undefined) {
        const cacheInfo = "Size: " + formatNumber(mostRecentData?.["cache-size"]);
        message = mostRecentData.additionalStatisticsInfo ? message : cacheInfo + ", " + message;
    }
    return message;
};

export const NodeStats = ({ stats }) => {
    const statValue = getStat(stats);
    const currentLayout = useAppSelector(getCurrentLayout);
    if (!stats) return null;
    return <Box sx={statStyle(stats.type, currentLayout)}>{statValue}</Box>;
};

type NodeHandleProps = {
    sourcePosition?: Position;
    targetPosition?: Position;
    nodeId?: string;
};

export const NodeHandle = ({ sourcePosition, targetPosition, nodeId }: NodeHandleProps) => {
    const edges = useAppSelector(getEdges);
    const status = getAppStatus(store.getState());
    const updateNodeInternals = useUpdateNodeInternals();

    // Check if this node has any connections
    const hasIncomingConnection = edges?.some(edge => edge.target === nodeId) || false;
    const hasOutgoingConnection = edges?.some(edge => edge.source === nodeId) || false;

    // Notify React Flow when handles appear/disappear so edge paths are recomputed
    useEffect(() => {
        if (nodeId) updateNodeInternals(nodeId);
    }, [hasIncomingConnection, hasOutgoingConnection, nodeId]);

    return (
        <Box sx={handleStyles(sourcePosition, status)}>
            {hasIncomingConnection && (
                <Handle className="target-handle" type="target" position={targetPosition || Position.Top} />
            )}
            {hasOutgoingConnection && (
                <Handle className="source-handle" type="source" position={sourcePosition || Position.Bottom} />
            )}
        </Box>
    );
};

export const showStats = appStatus => {
    return appStatus === "RUNNING";
};

export const ErrorStatus = () => {
    return (
        <Box sx={errorIconStyles} data-test-id="node-icon-invalid">
            <ErrorIcon />
        </Box>
    );
};
